import {
  CHART_COPIED,
  CHART_CREATED,
  CHART_DELETED,
  ICON_COPIED,
  ICON_CREATED,
  ICON_DELETED,
  IMAGE_COPIED,
  IMAGE_CREATED,
  IMAGE_DELETED,
  LINE_COPIED,
  LINE_CREATED,
  LINE_DELETED,
  MAP_COPIED,
  MAP_CREATED,
  MAP_DELETED,
  QR_CODE_CREATED,
  QR_CODE_DELETED,
  SHAPE_COPIED,
  SHAPE_CREATED,
  SHAPE_DELETED,
  TABLE_COPIED,
  TABLE_CREATED,
  TABLE_DELETED,
  TEXT_COPIED,
  TEXT_CREATED,
  TEXT_DELETED,
} from 'constants/mixpanel';
import { MapType } from 'modules/Editor/components/WidgetMenu/SideMenu/MapWidgetMenu/MapWidgetMenu.types';
import { UserType, UserTypes } from 'modules/User';
import { WidgetType } from 'types/widget.types';
import { isDevelopment, isStagingDomain } from 'utils/environment';
import { BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { getChartInfo } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { IconWidgetData } from 'widgets/IconWidget/IconWidget.types';
import { LineWidgetData, LineWidgetTypes } from 'widgets/Lines/LineWidget/LineWidget.types';
import { Pos } from 'widgets/Lines/LineWidget/LineWidget.types';
import { MapWidgetData, ScaleType } from 'widgets/MapWidget/MapWidget.types';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { AllWidgetData } from 'widgets/Widget.types';

import { Mixpanel } from './mixpanel';

// Get string for 'User Plan' mp property
export const getUserPlanProperty = (userType: UserType) => (userType === UserTypes.Free ? 'free' : userType);

export function generateToolbarName(widgetType: WidgetType) {
  let name;
  switch (widgetType) {
    case WidgetType.Icon:
      name = 'Icon Toolbar';
      break;
    case WidgetType.BasicShape:
      name = 'Shape Toolbar';
      break;
    case WidgetType.Image:
      name = 'Image Toolbar';
      break;
    case WidgetType.Line:
      name = 'Line Toolbar';
      break;
    case WidgetType.AreaChart:
    case WidgetType.BarChart:
    case WidgetType.ColumnChart:
    case WidgetType.ComboChart:
    case WidgetType.HalfPieChart:
    case WidgetType.HistogramChart:
    case WidgetType.LineChart:
    case WidgetType.PieChart:
    case WidgetType.StackedAreaChart:
    case WidgetType.StackedBarChart:
    case WidgetType.StackedColumnChart:
    case WidgetType.WordCloudChart:
      name = 'Chart Toolbar';
      break;
    case WidgetType.MapChart:
      name = 'Map Toolbar';
      break;
    default:
      if (isDevelopment || isStagingDomain) {
        console.error('MP Events: missing the toolbar name when opening the alt text panel');
      }
      name = 'Unknown';
      break;
  }

  return name;
}

const noColorArray = ['transparent', '#00000000'];

// helper function to generate fill prop for basic shape widget
const isNoColor = (fillColor: string[], fillPercent: number) => {
  if (fillColor.length === 1 && noColorArray.includes(fillColor[0])) return true;
  if (fillPercent === 0 && fillColor.length === 2 && noColorArray.includes(fillColor[0])) return true;
  if (fillPercent === 100 && fillColor.length === 2 && noColorArray.includes(fillColor[1])) return true;
  if (fillColor.length === 2 && noColorArray.includes(fillColor[0]) && noColorArray.includes(fillColor[1])) return true;
  return false;
};
// helper function to generate border prop for basic shape widget
const getShapeBorder = (borderWidth: number, borderStyle: string) => {
  if (borderWidth === 0) return SHAPE_CREATED.border.None;
  if (borderStyle === 'solid') return SHAPE_CREATED.border.Solid;
  if (borderStyle === 'dashed') return SHAPE_CREATED.border.Dashed;
  if (borderStyle === 'dotted') return SHAPE_CREATED.border.Dotted;
};
// helper function to generate orientation prop for line widget
export const getLineOrientation = (startPos: Pos, endPos: Pos) => {
  if (startPos.yPx === endPos.yPx) return LINE_CREATED.orientation.Horizontal;
  if (startPos.xPx === endPos.xPx) return LINE_CREATED.orientation.Vertical;
  return LINE_CREATED.orientation.Diagonal;
};
export const getLineType = (lineType: LineWidgetTypes) => {
  switch (lineType) {
    case LineWidgetTypes.stepped:
      return LINE_CREATED.type.Stepped;
    case LineWidgetTypes.straight:
      return LINE_CREATED.type.Straight;
    case LineWidgetTypes.curved:
      return LINE_CREATED.type.Curved;
    default:
      return LINE_CREATED.type.Straight;
  }
};

/**
 * Fire mixpanel event tracker.
 *
 * @param widgetType Copied widget type.
 * @param from Source of copy event.
 */
export const fireMPOnCopyWidget = (widgetType: WidgetType, from: 'Keyboard' | 'Context Menu' = 'Context Menu') => {
  switch (widgetType) {
    case WidgetType.BasicShape:
      Mixpanel.track(SHAPE_COPIED.eventName, { from });
      break;
    case WidgetType.Line:
      Mixpanel.track(LINE_COPIED.eventName, { from });
      break;
    case WidgetType.Icon:
      Mixpanel.track(ICON_COPIED.eventName, { from });
      break;
    case WidgetType.Image:
      Mixpanel.track(IMAGE_COPIED.eventName, { from });
      break;
    case WidgetType.Table:
      Mixpanel.track(TABLE_COPIED.eventName, { from });
      break;
    case WidgetType.AreaChart:
    case WidgetType.BarChart:
    case WidgetType.ColumnChart:
    case WidgetType.ComboChart:
    case WidgetType.HalfPieChart:
    case WidgetType.HistogramChart:
    case WidgetType.LineChart:
    case WidgetType.PieChart:
    case WidgetType.StackedAreaChart:
    case WidgetType.StackedBarChart:
    case WidgetType.StackedColumnChart:
    case WidgetType.StatChart:
    case WidgetType.WordCloudChart:
      Mixpanel.track(CHART_COPIED.eventName, { from });
      break;
    case WidgetType.Text:
      Mixpanel.track(TEXT_COPIED.eventName, { from });
      break;
    case WidgetType.MapChart:
      Mixpanel.track(MAP_COPIED.eventName, { from });
      break;
    default:
      if (isDevelopment) {
        console.error('MP Events: missing the widget copy event for widget ', widgetType);
      }
      break;
  }
};

export const fireMPOnDuplicateWidget = (
  widgetType: WidgetType,
  widgetData: AllWidgetData,
  from: 'Duplication' | 'Context Menu Paste' | 'Keyboard Paste' = 'Duplication',
) => {
  switch (widgetType) {
    case WidgetType.BasicShape:
      const { type, border, fillColor, fillPercent } = widgetData as BasicShapeWidgetData;
      Mixpanel.track(SHAPE_CREATED.eventName, {
        type,
        fill: !isNoColor(fillColor, fillPercent),
        border: getShapeBorder(border.width, border.style),
        from,
      });
      break;
    case WidgetType.Line:
      const { startPos, endPos, startArrowStyle, endArrowStyle } = widgetData as LineWidgetData;
      Mixpanel.track(LINE_CREATED.eventName, {
        orientation: getLineOrientation(startPos, endPos),
        line_start: startArrowStyle === 'none' ? LINE_CREATED.lineEdge.None : LINE_CREATED.lineEdge.BasicArrow,
        line_end: endArrowStyle === 'none' ? LINE_CREATED.lineEdge.None : LINE_CREATED.lineEdge.BasicArrow,
        type: getLineType((widgetData as LineWidgetData).type),
        from,
      });
      break;
    case WidgetType.Icon:
      const { iconId } = widgetData as IconWidgetData;
      Mixpanel.track(ICON_CREATED.eventName, {
        from: from || ICON_CREATED.from.Duplication,
        icon_id: iconId,
      });
      break;
    case WidgetType.Image:
      Mixpanel.track(IMAGE_CREATED.eventName, {
        from,
      });
      break;
    case WidgetType.Table:
      Mixpanel.track(TABLE_CREATED.eventName, {
        from,
      });
      break;
    case WidgetType.AreaChart:
    case WidgetType.BarChart:
    case WidgetType.ColumnChart:
    case WidgetType.ComboChart:
    case WidgetType.HalfPieChart:
    case WidgetType.HistogramChart:
    case WidgetType.LineChart:
    case WidgetType.PieChart:
    case WidgetType.StackedAreaChart:
    case WidgetType.StackedBarChart:
    case WidgetType.StackedColumnChart:
    case WidgetType.StatChart:
    case WidgetType.WordCloudChart:
      const { widgetTypeName, preset } = getChartInfo(widgetType, widgetData);

      Mixpanel.track(CHART_CREATED.eventName, {
        from,
        preset,
        chart_type: widgetTypeName,
      });
      break;
    case WidgetType.Text:
      const { textTag } = widgetData as TextWidgetData;
      const textType = textTag || 'Other';

      Mixpanel.track(TEXT_CREATED.eventName, {
        from,
        text_style: textType.replace(textType[0], textType[0].toUpperCase()),
      });
      break;
    case WidgetType.QRCode:
      Mixpanel.track(QR_CODE_CREATED.eventName, {
        from,
      });
      break;
    case WidgetType.MapChart:
      // Previously the region name wasn't recorded for each widget
      const { region = 'Previously untracked' } = widgetData as MapWidgetData;
      Mixpanel.track(MAP_CREATED.eventName, {
        from,
        region,
      });
      break;
    default:
      if (isDevelopment) {
        console.error('MP Events: missing the widget duplication event for widget ', widgetType);
      }
      break;
  }
};

export const fireMPOnDeleteWidget = (widgetType: WidgetType, widgetData: AllWidgetData) => {
  switch (widgetType) {
    case WidgetType.Text:
      const { textTag } = widgetData as TextWidgetData;
      Mixpanel.track(TEXT_DELETED.eventName, { text_tag: textTag });
      break;
    case WidgetType.BasicShape:
      const { type } = widgetData as BasicShapeWidgetData;
      Mixpanel.track(SHAPE_DELETED.eventName, { shape_id: type });
      break;
    case WidgetType.Line:
      Mixpanel.track(LINE_DELETED.eventName);
      break;
    case WidgetType.LineIcon:
    case WidgetType.Icon:
      const { iconId } = widgetData as IconWidgetData;
      Mixpanel.track(ICON_DELETED.eventName, { icon_id: iconId });
      break;
    case WidgetType.Image:
      Mixpanel.track(IMAGE_DELETED.eventName);
      break;
    case WidgetType.Table:
      Mixpanel.track(TABLE_DELETED.eventName);
      break;
    case WidgetType.AreaChart:
    case WidgetType.BarChart:
    case WidgetType.ComboChart:
    case WidgetType.ColumnChart:
    case WidgetType.HalfPieChart:
    case WidgetType.HistogramChart:
    case WidgetType.LineChart:
    case WidgetType.PieChart:
    case WidgetType.StackedAreaChart:
    case WidgetType.StackedBarChart:
    case WidgetType.StackedColumnChart:
    case WidgetType.StatChart:
    case WidgetType.WordCloudChart:
      const { widgetTypeName } = getChartInfo(widgetType, widgetData);
      Mixpanel.track(CHART_DELETED.eventName, { chart_type: widgetTypeName });
      break;
    case WidgetType.QRCode:
      Mixpanel.track(QR_CODE_DELETED.eventName);
      break;
    case WidgetType.MapChart:
      const { mapType, scaleType = ScaleType.Steps } = widgetData as MapWidgetData;
      Mixpanel.track(MAP_DELETED.eventName, {
        map_type: getMapTypeForEvent(MAP_DELETED, mapType, scaleType),
      });
      break;
    default:
      if (isDevelopment) {
        console.error('MP Events: missing the widget delete event for widget ', widgetType);
      }
      break;
  }
};

type MapWidgetMixpanelEvent = {
  mapType: {
    ChoroplethSteps: string;
    ChoroplethGradient: string;
    Bubble: string;
    Monochrome: string;
  };
};

/**
 * Get the map type for an event based on map and scale types.
 *
 * @param {MapWidgetMixpanelEvent} event - The map widget event.
 * @param {MapType} mapType - The map type.
 * @param {ScaleType} scaleType - The scale type.
 * @returns {string} The map type for the edit event.
 */
export const getMapTypeForEvent = (event: MapWidgetMixpanelEvent, mapType: MapType, scaleType: ScaleType) => {
  if (mapType === MapType.Choropleth) {
    if (scaleType === ScaleType.Steps) {
      return event.mapType.ChoroplethSteps;
    } else if (scaleType === ScaleType.Gradient) {
      return event.mapType.ChoroplethGradient;
    }
  } else if (mapType === MapType.Bubble) {
    return event.mapType.Bubble;
  } else if (mapType === MapType.Monochrome) {
    return event.mapType.Monochrome;
  }
};
